import { useState, useEffect, useRef, useCallback } from 'react';

interface ScrollSpyOptions {
    /** Root element to observe within (default: viewport) */
    root?: Element | null;
    /** Margin around the root (CSS margin format) */
    rootMargin?: string;
    /** Minimum visibility threshold (0-1) */
    threshold?: number;
}

/**
 * Hook to track which sections are currently visible in the viewport.
 * Returns the ID of the most visible (topmost) section.
 */
export function useScrollSpy(
    sectionIds: string[],
    options: ScrollSpyOptions = {}
): string | null {
    const [activeId, setActiveId] = useState<string | null>(null);
    const visibleSectionsRef = useRef<Map<string, IntersectionObserverEntry>>(new Map());

    const { root = null, rootMargin = '-10% 0px -70% 0px', threshold = 0.1 } = options;

    const updateActiveId = useCallback(() => {
        // Find the topmost visible section
        let topmost: { id: string; top: number } | null = null;

        visibleSectionsRef.current.forEach((entry, id) => {
            if (entry.isIntersecting) {
                const rect = entry.boundingClientRect;
                if (!topmost || rect.top < topmost.top) {
                    topmost = { id, top: rect.top };
                }
            }
        });

        setActiveId(topmost?.id ?? null);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const id = entry.target.getAttribute('data-toc-id');
                    if (id) {
                        visibleSectionsRef.current.set(id, entry);
                    }
                });
                updateActiveId();
            },
            { root, rootMargin, threshold }
        );

        // Observe all sections
        sectionIds.forEach((id) => {
            const element = document.querySelector(`[data-toc-id="${id}"]`);
            if (element) {
                observer.observe(element);
            }
        });

        return () => {
            observer.disconnect();
            visibleSectionsRef.current.clear();
        };
    }, [sectionIds, root, rootMargin, threshold, updateActiveId]);

    return activeId;
}

export default useScrollSpy;
